import { faExclamationTriangle, faInbox, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AgGridAtom } from "../../../../components/atoms/atom";
import ModalAtom from "../../../../components/atoms/modal/modal";
import "./InvoiceModals.scss";
import { getHistoricalInvoicesColumnDefs } from "./invoiceSharedUtils";
import PreviousInvoicesTable from "./PreviousInvoicesTable";

/**
 * Collection of modals used in the invoice creation flow
 */
function InvoiceModals({
    formValues,
    previousInvoices = [],
    showConfirmationModal,
    showHistoricalInvoicesModal,
    showPreviousInvoicesModal,
    showPreviousInvoiceHistoricalModal,
    selectedPreviousInvoice,
    onCloseConfirmation,
    onConfirmHistoricalData,
    onCloseHistoricalInvoices,
    onClosePreviousInvoices,
    onClosePreviousInvoiceHistorical,
    onViewHistoricalInvoices
}) {
    const { t } = useTranslation();

    // Use shared column definitions for historical invoices AgGrid
    const historicalColumnDefs = useMemo(() => getHistoricalInvoicesColumnDefs(t), [t]);

    return (
        <>
            {/* Historical Data Confirmation Modal */}
            <ModalAtom
                show={showConfirmationModal}
                handleClose={onCloseConfirmation}
                handleSubmit={onConfirmHistoricalData}
                title={
                    formValues.isHistoricalBaseline
                        ? t("invoice.confirm-historical-baseline")
                        : t("invoice.confirm-first-invoice")
                }
                saveText="common.confirm"
                centered
                body={
                    <>
                        {formValues.isHistoricalBaseline ? (
                            <>
                                <div className="alert alert-warning">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                    {t("invoice.historical-baseline-warning")}
                                </div>
                                <p>{t("invoice.historical-baseline-confirmation-message", {
                                    count: formValues.historicalInvoices?.length || 0,
                                    nextNumber: (formValues.historicalInvoices?.length || 0) + 1
                                })}</p>
                                <div className="mt-3 p-3 bg-light rounded">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                        {t("invoice.historical-baseline-info")}
                                    </small>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="alert alert-info">
                                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                    {t("invoice.first-invoice-info")}
                                </div>
                                <p>{t("invoice.first-invoice-confirmation-message")}</p>
                                <div className="mt-3 p-3 bg-light rounded">
                                    <small className="text-muted">
                                        {t("invoice.first-invoice-details")}
                                    </small>
                                </div>
                            </>
                        )}
                    </>
                }
            />

            {/* Historical Invoices Details Modal */}
            <ModalAtom
                show={showHistoricalInvoicesModal}
                handleClose={onCloseHistoricalInvoices}
                title={t("invoice.historical-invoices-details")}
                size="xl"
                centered
                hideFooter
                body={
                    <>
                        <div className="alert alert-info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {t("invoice.historical-data-description")}
                        </div>
                        {formValues.historicalInvoices && formValues.historicalInvoices.length > 0 ? (
                            <div className="mb-3 im-grid-container">
                                <AgGridAtom
                                    columnDefs={historicalColumnDefs}
                                    rowData={formValues.historicalInvoices}
                                    domLayout="normal"
                                />
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <FontAwesomeIcon icon={faInbox} className="fs-1" />
                                <p className="mt-2">{t("invoice.no-historical-invoices")}</p>
                            </div>
                        )}
                    </>
                }
            />

            {/* Previous Invoices Details Modal */}
            <ModalAtom
                show={showPreviousInvoicesModal}
                handleClose={onClosePreviousInvoices}
                title={`${formValues.selectedTicketCategory?.label || t("invoice.ticket-category")} - ${t("invoice.previous-invoices-details")}`}
                size="xl"
                centered
                hideFooter
                body={
                    <>
                        <div className="alert alert-info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {t("invoice.previous-invoices-description")}
                        </div>
                        <div className="im-previous-invoices">
                            <PreviousInvoicesTable
                                invoices={previousInvoices}
                                onHistoricalDataClick={onViewHistoricalInvoices}
                            />
                        </div>
                    </>
                }
            />

            {/* Previous Invoice's Historical Data Modal */}
            <ModalAtom
                show={showPreviousInvoiceHistoricalModal}
                handleClose={onClosePreviousInvoiceHistorical}
                title={`${selectedPreviousInvoice?.invoiceNumber || t("invoice.invoice")} - ${t("invoice.historical-invoices-details")}`}
                size="xl"
                centered
                hideFooter
                body={
                    <>
                        <div className="alert alert-info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {t("invoice.historical-data-description")}
                        </div>
                        {selectedPreviousInvoice?.historicalInvoices && selectedPreviousInvoice.historicalInvoices.length > 0 ? (
                            <div className="mb-3 im-grid-container">
                                <AgGridAtom
                                    columnDefs={historicalColumnDefs}
                                    rowData={selectedPreviousInvoice.historicalInvoices}
                                    domLayout="normal"
                                    rowHeight={80}
                                />
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <FontAwesomeIcon icon={faInbox} className="fs-1" />
                                <p className="mt-2">{t("invoice.no-historical-invoices")}</p>
                            </div>
                        )}
                    </>
                }
            />
        </>
    );
}

export default InvoiceModals;
